/**
 * Piloting a camera, as in Unreal: the viewport looks through a camera entity
 * (its pose and field of view), and navigating the viewport moves the camera.
 * Each burst of navigation (moves with no pause longer than PILOT_BURST_MS)
 * writes the camera's Transform as one undo step, through `entity.setTransform`
 * like a gizmo drag. When the camera moves another way (undo, the Inspector),
 * the view follows it.
 *
 * It owns only which camera is piloted; the view stays the ViewportCamera's.
 * Stopping gives the viewport its own field of view back and steps it back a
 * little, so the camera it just left is in view rather than around the eye.
 */
import type { InteractionSession } from "../../commands/CommandBus";
import { parsePayload } from "../../commands/payload";
import type { CommandDefinition, CommandRequest, CommandResult } from "../../commands/types";
import type { ChangeScheduler } from "../../core/ChangeScheduler";
import { mat4Invert } from "../../core/math/mat4";
import type { Mat4 } from "../../core/math/mat4";
import { vec3Scale, vec3Sub } from "../../core/math/vec3";
import { VersionedStore } from "../../core/store";
import type { ReadableStore } from "../../core/store";
import type { Timers } from "../../core/Timers";
import type { EntityId } from "../../world/entity";
import { localTransformAt } from "../../world/localTransform";
import type { TransformSystem } from "../../world/TransformSystem";
import type { WorldReader } from "../../world/World";
import { forwardOf, lookingThrough, poseFromMatrix, viewMatrix } from "./cameraMath";
import type { ViewCamera } from "./cameraMath";
import type { ViewportCamera } from "./ViewportCamera";

/** A pause in navigation this long ends one undo step; the next move starts another. */
export const PILOT_BURST_MS = 500;
/** How far the viewport steps back along its view when piloting stops, in meters. */
const EJECT_STEP_BACK = 1.5;
/** Poses closer than this are the same (meters and degrees): writes round to 1e-5. */
const SAME_POSE = 1e-3;

export interface CameraPilotDeps {
  readonly camera: ViewportCamera;
  readonly world: WorldReader;
  readonly transforms: TransformSystem;
  readonly bus: {
    execute(request: CommandRequest): CommandResult;
    openSession(name: string, onAbort: () => void): InteractionSession;
  };
  readonly timers: Timers;
}

export class CameraPilot implements ReadableStore<EntityId | null> {
  private readonly store: VersionedStore<EntityId | null>;
  /** The viewport's own field of view, given back when piloting stops. */
  private viewportFov = 0;
  private session: { readonly id: string; readonly interaction: InteractionSession } | null = null;
  private sessions = 0;
  private cancelBurst: (() => void) | null = null;
  private readonly unsubscribe: (() => void)[];

  constructor(
    private readonly deps: CameraPilotDeps,
    scheduler: ChangeScheduler,
  ) {
    this.store = new VersionedStore<EntityId | null>(scheduler, null);
    this.unsubscribe = [
      deps.camera.subscribe(() => {
        this.onViewChanged();
      }),
      deps.world.events.on("entity:changed", (event) => {
        if (event.id === this.getSnapshot() && event.next === null) this.stop();
        else this.followCamera();
      }),
      deps.world.events.on("component:changed", () => {
        this.followCamera();
      }),
      deps.world.events.on("world:replaced", () => {
        this.stop();
      }),
    ];
  }

  readonly subscribe = (listener: () => void) => this.store.subscribe(listener);
  readonly getSnapshot = (): EntityId | null => this.store.getSnapshot();

  /** Looks through `entity` (a camera) and moves it with the viewport from now on. */
  pilot(entity: EntityId): void {
    if (this.getSnapshot() === entity) return;
    if (this.getSnapshot() === null) this.viewportFov = this.deps.camera.getSnapshot().fov;
    else this.endBurst();

    this.store.publish(entity);
    this.followCamera();
  }

  /** Stops piloting: the viewport's own field of view, a step behind where the camera is. */
  stop(): void {
    if (this.getSnapshot() === null) return;
    this.endBurst();
    // Cleared first, so stepping back doesn't move the camera.
    this.store.publish(null);
    this.deps.camera.navigate((view) => ({
      ...view,
      position: vec3Sub(view.position, vec3Scale(forwardOf(view), EJECT_STEP_BACK)),
      fov: this.viewportFov,
    }));
  }

  dispose(): void {
    this.endBurst();
    for (const off of this.unsubscribe) off();
  }

  // ─── The two directions ─────────────────────────────────────────────────

  /** The viewport moved: move the camera to it (unless it's already there). */
  private onViewChanged(): void {
    const entity = this.getSnapshot();
    if (entity === null) return;
    const view = this.deps.camera.getSnapshot();
    // Top, Front, … and orthographic views aren't a camera's view: they end piloting.
    if (view.projection !== "perspective") {
      this.stop();
      return;
    }

    const record = this.deps.world.get(entity);
    const transform = record?.components.get("Transform");
    if (!record || !transform) return;
    if (samePose(this.cameraWorld(entity), view)) return;

    const world = mat4Invert(viewMatrix(view));
    if (!world) return;
    const parentWorld = record.parent ? this.deps.transforms.worldMatrix(record.parent) : null;
    const local = localTransformAt(
      world,
      parentWorld,
      transform.rotation as [number, number, number],
    );
    this.deps.bus.execute({
      type: "entity.setTransform",
      payload: { entity, ...local, scale: transform.scale, session: this.burst() },
      origin: "viewport",
    });
  }

  /** The camera moved (or its field of view changed): bring the view to it. */
  private followCamera(): void {
    const entity = this.getSnapshot();
    if (entity === null) return;
    const camera = this.deps.world.component(entity, "Camera");
    if (!camera) {
      this.stop();
      return;
    }

    const world = this.cameraWorld(entity);
    const view = this.deps.camera.getSnapshot();
    if (samePose(world, view) && view.fov === camera.fov) return;
    this.deps.camera.navigate((current) => lookingThrough(current, world, camera.fov as number));
  }

  private cameraWorld(entity: EntityId): Mat4 {
    return this.deps.transforms.worldMatrix(entity);
  }

  // ─── Undo steps ─────────────────────────────────────────────────────────

  /** The session id for this burst of navigation, starting one if needed. */
  private burst(): string {
    if (!this.session?.interaction.active) {
      const interaction = this.deps.bus.openSession("pilot", () => {
        this.session = null;
      });
      this.session = { id: `pilot:${++this.sessions}`, interaction };
    }
    this.cancelBurst?.();
    this.cancelBurst = this.deps.timers.after(PILOT_BURST_MS, () => {
      this.endBurst();
    });
    return this.session.id;
  }

  private endBurst(): void {
    this.cancelBurst?.();
    this.cancelBurst = null;
    this.session?.interaction.close();
    this.session = null;
  }
}

/** Whether a camera's world pose is where the view already is. */
function samePose(world: Mat4, view: ViewCamera): boolean {
  const pose = poseFromMatrix(world);
  const moved = pose.position.some((n, i) => Math.abs(n - (view.position[i] ?? 0)) > SAME_POSE);
  return (
    !moved &&
    angleBetween(pose.yaw, view.yaw) <= SAME_POSE &&
    Math.abs(pose.pitch - view.pitch) <= SAME_POSE
  );
}

/** The smaller angle between two headings in degrees, so 359° and -1° are the same. */
function angleBetween(a: number, b: number): number {
  const turn = (((a - b) % 360) + 360) % 360;
  return Math.min(turn, 360 - turn);
}

/** `camera.pilot { entity }`: look through a camera and move it (null stops). */
export function pilotCommands(pilot: CameraPilot, world: WorldReader): CommandDefinition[] {
  const definition: CommandDefinition<{ entity: EntityId | null }, undefined> = {
    type: "camera.pilot",
    undoable: false,
    validate: (raw) => {
      const shape = parsePayload(raw, { entity: "unknown" });
      if (!shape.ok) return shape;
      const { entity } = shape.value;
      if (entity === null) return { ok: true, value: { entity: null } };
      if (typeof entity !== "string") return { ok: false, error: "entity must be an id or null" };
      const record = world.get(entity as EntityId);
      if (!record) return { ok: false, error: `No entity '${entity}'` };
      if (!record.components.has("Camera"))
        return { ok: false, error: `${record.name} isn't a camera` };
      return { ok: true, value: { entity: record.id } };
    },
    do: ({ entity }) => {
      if (entity === null) pilot.stop();
      else pilot.pilot(entity);
      return undefined;
    },
    undo: () => undefined,
    describe: ({ entity }) =>
      entity === null ? "Stop piloting" : `Pilot ${world.get(entity)?.name ?? entity}`,
  };
  return [definition as CommandDefinition];
}
